import {
  DeleteIcon,
  LocalizationString,
  WarningIcon,
} from '@celito.clients/assets';
import {
  ActionTypeEnum,
  CustomViewTypeEnum,
  ObjectEnum,
  ROUTES,
} from '@celito.clients/enums';
import {
  useActiveModule,
  useUpdateInitiationConfirmationModal,
  useURLParams,
} from '@celito.clients/hooks';
import {
  DelegationModal,
  deleteObject,
  LifeCycleStageUserActionsPermissionEnum,
  RecordAction,
  triggerWorkflow,
} from '@celito.clients/list-view-engine';
import { ModalContext } from '@celito.clients/provider';
import {
  ConfirmDialog,
  FluentIconNames,
  Icon,
  Menu,
  useBreadcrumbs,
  UserActionPopup,
} from '@celito.clients/shared';
import {
  ObjectActionDefinition,
  ObjectAttributeType,
} from '@celito.clients/types';
import {
  createTestAttribute,
  infoToast,
  isExistInUrl,
  raiseErrorToast,
  successToast,
} from '@celito.clients/utils';
import {
  Button,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import { ObjectTemplatesSubMenu } from 'libs/list-view-engine/src/lib/components/action-column/object-template-menu/object-template-menu';
import { ExtensionRequestDialog } from 'libs/list-view-engine/src/lib/components/extension-request-dialog/extension-request-dialog.component';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useFormEngineContext } from '../../hooks';
import { getFormValues } from '../../utils/helper';
import { executeAction } from '../actions/actions.service';
import { contextMenuStyles } from './context-menu-actions-component.styles';

interface ContextMenuProps {
  recordData: Record<string, unknown>;
  actions: ObjectActionDefinition[];
  version: string;
  objectMetadata: ObjectAttributeType;
  response: any;
  refetchRecordData?: () => Promise<void>;
  objectLabel?: string;
  recordDetails?: Record<string, unknown>;
  attributeConfig?: ObjectAttributeType;
}

export const ContextMenu = (props: ContextMenuProps): JSX.Element | null => {
  const {
    recordData,
    objectMetadata,
    response,
    refetchRecordData,
    objectLabel,
  } = props;

  const objectName = objectMetadata?.name;
  const recordName = recordData?.name as string;

  const [openDelegateModal, setOpenDelegateModal] = useState(false);
  const [openExtensionRequestDialog, setOpenExtensionRequestDialog] =
    useState(false);
  const [extensionRequestActionType, setExtensionRequestActionType] = useState<
    ActionTypeEnum.REQUEST_EXTENSION | ActionTypeEnum.CANCEL_EXTENSION | null
  >(null);
  const newStyles = contextMenuStyles();
  const navigate = useNavigate();
  const { urlParams } = useURLParams();
  const taskName = urlParams.get('taskName');
  const activeModule = useActiveModule();
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isShowText, setIsShowText] = useState(false);
  const [respectiveUserActionRes, setRespectiveUserActionRes] =
    useState<RecordAction>();
  const [nameAndVersion, setNameAndVersion] = useState({
    version: '',
    name: '',
  });
  const { addBreadcrumb, replaceLastBreadcrumb } = useBreadcrumbs();
  const isUnsubmittedControlledDocsListView =
    isExistInUrl(CustomViewTypeEnum.UNSUBMITTED_CONTROL_DOCS_LIST_VIEW) ||
    isExistInUrl('cd_unsubmitted_controlled_documents__a');

  const isULModule = window.location.pathname.includes('lms__a');
  const { openModal } = useContext(ModalContext);
  const { fieldsState } = useFormEngineContext();

  const formFieldsVisibleAndEditable = useMemo(() => {
    const result: string[] = [];
    Object.keys(fieldsState).forEach((fieldName) => {
      const state = fieldsState[fieldName];
      if (state?.isVisible && state?.isEditable) {
        result.push(fieldName);
      }
    });
    return result;
  }, [fieldsState]);

  const updateInitiationConfirmationProps =
    useUpdateInitiationConfirmationModal({
      objectName: objectName as ObjectEnum,
    });

  const {
    showUpdateInititationConfirmationModal,
    setShowUpdateInititationConfirmationModal,
    handleOnUpdateInitiationConfirmation,
    handleOnUpdateInitiate,
    loading,
  } = updateInitiationConfirmationProps;

  const openDeleteAlert = () => {
    setShowDeleteAlert(true);
  };
  const openEsignModal = (
    recordData?: Record<string, unknown>,
    contextMenuAction?: Record<string, unknown>,
    taskPayload?: Record<string, unknown>
  ) => {
    openModal(
      '',
      taskPayload,
      activeModule?.link,
      recordData,
      contextMenuAction
    );
  };

  const handleLifecycleStateChange = async (
    actionName: string,
    recordName: string,
    recordVersion: string
  ) => {
    try {
      await executeAction(actionName, recordName, recordVersion, {});
      successToast({ message: LocalizationString.LIFECYCLE_STATE_CHANGED });
      refetchRecordData?.();
    } catch (error) {
      raiseErrorToast(error);
    }
  };

  const callUserActions = async (action: RecordAction) => {
    if (action.actionType?.toLowerCase() === 'delete') {
      openDeleteAlert();
      return;
    }

    if (action.actionType?.toLowerCase() === 'delegate') {
      setOpenDelegateModal(true);
      return;
    }

    if (
      action.actionType === ActionTypeEnum.REQUEST_EXTENSION ||
      action.actionType === ActionTypeEnum.CANCEL_EXTENSION
    ) {
      setExtensionRequestActionType(action.actionType);
      setOpenExtensionRequestDialog(true);
      return;
    }

    const objectname = action?.objectName;
    const version = response?.version;

    if (action.actionType === ActionTypeEnum.ACTIVATE && recordName) {
      infoToast({ message: `Activating ${response?.title ?? recordName}` });
      await callTriggerWorkflow(
        action.name,
        recordName,
        version,
        action?.actionType,
        true,
        response?.title
      );

      return;
    }

    if (action.actionType === ActionTypeEnum.DEACTIVATE && recordName) {
      infoToast({
        message: `Deactivating ${response?.title ?? recordName}`,
      });
      await callTriggerWorkflow(
        action.name,
        recordName,
        version,
        action?.actionType,
        true,
        response?.title
      );

      return;
    }

    if (
      [ActionTypeEnum.TRIGGER_WORKFLOW].includes(
        action.actionType as ActionTypeEnum
      ) &&
      recordName &&
      version
    ) {
      callTriggerWorkflow(action.name, recordName, version, action.actionType!);
      openEsignModal(response, action as unknown as Record<string, unknown>, {
        formFieldsVisibleAndEditable,
      });
      return;
    }

    if (
      action.actionType === ActionTypeEnum.LIST &&
      action.label?.toLowerCase()?.includes('audit')
    ) {
      const url = `../${action.actionType?.toLowerCase()}/${
        action.viewName
      }?recordname=${recordName}&objectname=${objectName}`;
      navigate(addBreadcrumb({ label: action.label ?? '', path: url }));
      return;
    }

    if (
      action.actionType?.toLowerCase() === 'list' &&
      recordName &&
      objectname
    ) {
      const url = `../${action.actionType?.toLowerCase()}/${
        action.viewName
      }?recordname=${recordName}&objectname=${objectname}`;
      navigate(addBreadcrumb({ label: action.label ?? '', path: url }));

      return;
    }

    if (action.actionType?.toLowerCase() === 'custom') {
      if (action.name === 'complete_course_action__a') {
        navigate(
          `../${ROUTES.DOCUMENT_VIEW}?training_assignment_name=${recordName}&openCompleteModal=true`
        );
        return;
      }
    }

    if (action.actionType?.toLowerCase() === 'customview') {
      if (response?.detailViewName?.includes?.('custom/')) {
        navigate(
          `../view/${response?.detailViewName}/${recordName}?taskName=${taskName}`
        );
        return;
      }
      navigate(
        `../customview/${response?.detailViewName}/${recordName}?taskName=${taskName}`
      );
      return;
    }

    if (action.actionType === ActionTypeEnum.UPDATE) {
      handleOnUpdateInitiate(response ?? {}, action.viewName!);
      setShowUpdateInititationConfirmationModal(true);

      return;
    }

    if (isULModule && response?.version) {
      const url = `../${action.actionType?.toLowerCase()}/${
        action.viewName
      }/${recordName}${
        response?.version ? `?version=${response?.version}` : ''
      }`;

      navigate(
        replaceLastBreadcrumb({
          path: url,
          label: response?.label || response?.title || response?.name,
        }),
        { replace: action.actionType === ActionTypeEnum.EDIT }
      );
      return;
    }

    if (
      action.actionType === ActionTypeEnum.VIEW ||
      action.actionType === ActionTypeEnum.EDIT
    ) {
      if (taskName) {
        const url = `../${action.actionType?.toLowerCase()}/${
          action.viewName
        }/${recordName}?taskName=${taskName}${
          response?.initiationType
            ? `&requestType=${response?.initiationType}&version=${response?.version}`
            : ''
        }`;
        navigate(
          replaceLastBreadcrumb({
            label: response?.label || response?.title || response?.name,
            path: url,
          }),
          {
            replace: action.actionType === ActionTypeEnum.EDIT,
          }
        );
        return;
      }

      const url = `../${action.actionType?.toLowerCase()}/${
        action.viewName
      }/${recordName}${
        response?.initiationType
          ? `?requestType=${response?.initiationType}&version=${response?.version}`
          : ''
      }`;
      navigate(
        replaceLastBreadcrumb({
          label: response?.label || response?.title || response?.name,
          path: url,
        }),
        {
          replace: action.actionType === ActionTypeEnum.EDIT,
        }
      );
      return;
    }

    if (action.actionType === ActionTypeEnum.CHANGE_LIFECYCLE_STATE) {
      handleLifecycleStateChange(action.name, recordName, version);
      return;
    }

    if (action?.properties?.fields?.length) {
      if (action.actionType === ActionTypeEnum.CANCEL) {
        setRespectiveUserActionRes(action);
        setIsShowText(true);
        setIsConfirmDialogOpen(true);
        setNameAndVersion({
          version: version,
          name: recordName,
        });
      }
      return;
    }

    navigate(
      `../${action.actionType?.toLowerCase()}/${action.viewName}/${recordName}${
        response?.initiationType
          ? `?requestType=${response?.initiationType}`
          : ''
      }`
    );
  };

  const callTriggerWorkflow = async (
    actionName: string,
    recordName: string,
    version: string,
    actionType: string,
    shouldRefetch = false,
    title = ''
  ) => {
    try {
      await triggerWorkflow(actionName, recordName, version);
      if (actionType === ActionTypeEnum.DEACTIVATE) {
        successToast({
          message: `Deactivated ${title ?? recordName}`,
        });
      }
      if (actionType === ActionTypeEnum.ACTIVATE) {
        successToast({
          message: `Activated ${title ?? recordName}`,
        });
      }
      if (shouldRefetch) {
        refetchRecordData?.();
      }
    } catch (_error) {
      raiseErrorToast(_error);
    }
  };
  const closeDelegateModal = () => {
    setOpenDelegateModal(false);
  };

  const closeExtensionRequestDialog = () => {
    setOpenExtensionRequestDialog(false);
    setExtensionRequestActionType(null);
  };

  const deleteRecord = () => {
    deleteObject(props.objectMetadata.name, recordName)
      .then(() => {
        successToast({
          message: isUnsubmittedControlledDocsListView
            ? LocalizationString.DOCUMENT_INITIATION_DELETED
            : LocalizationString.DELETED_SUCCESSFULLY,
        });
        navigate(-1);
      })
      .catch((_error) => {
        raiseErrorToast(_error);
      });
  };

  const closeDeleteAlert = (deleteRec = false) => {
    if (deleteRec) {
      deleteRecord();
    }
    setShowDeleteAlert(false);
  };

  const handleOnConfirmPopUp = () => {
    setRespectiveUserActionRes(undefined);
    setIsConfirmDialogOpen(false);
    refetchRecordData?.();
  };

  const delegationModalRowItem = useMemo(
    () => ({
      ...props?.response,
      name: taskName,
    }),
    [props?.response, taskName]
  );

  return (
    <>
      <ConfirmDialog
        open={showDeleteAlert}
        onCancelClicked={(e) => {
          e.stopPropagation();
          closeDeleteAlert();
        }}
        onConfirmClicked={(e) => {
          e.stopPropagation();
          closeDeleteAlert(true);
        }}
        primaryButtonText={LocalizationString.DELETE}
        title={LocalizationString.DELETE_INITIATION}
        iconSrc={DeleteIcon}
        description={
          isUnsubmittedControlledDocsListView
            ? LocalizationString.ARE_YOU_SURE_YOU_WANT_TO_DELETE_DOC_INITIATION
            : LocalizationString.ARE_YOU_SURE_YOU_WANT_TO_DELETE
        }
        secondaryButtonText={LocalizationString.CANCEL}
        isBlocking
      />
      <ConfirmDialog
        dataTestId={`${createTestAttribute(
          LocalizationString.UPDATE_INITIATION_CONFIRMATION
        )}-modal`}
        open={showUpdateInititationConfirmationModal}
        onCancelClicked={() => setShowUpdateInititationConfirmationModal(false)}
        onConfirmClicked={handleOnUpdateInitiationConfirmation}
        primaryButtonText={LocalizationString.YES}
        secondaryButtonText={LocalizationString.NO}
        title={LocalizationString.UPDATE_INITIATION_CONFIRMATION}
        iconSrc={WarningIcon}
        description={LocalizationString.UPDATE_INITIATION_CONFIRMATION_MSG.replace(
          '{objectName}',
          objectLabel ?? 'record'
        )}
        isPrimaryButtonLoading={loading}
      />
      <Menu>
        <MenuTrigger>
          <Button
            className={newStyles.menuButton}
            data-testid={`button-more-action`}
          >
            <Icon iconName="MoreHorizontal24Regular" />
          </Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList className={newStyles.menuList}>
            {props?.actions
              .slice()
              .sort((a, b) => {
                const labelA = a.label || '';
                const labelB = b.label || '';
                return labelA.localeCompare(labelB);
              })
              .map((action, index) => {
                if (action.actionType === ActionTypeEnum.DOWNLOAD_TEMPLATE) {
                  return (
                    <ObjectTemplatesSubMenu
                      key={action.label}
                      menuItem={{
                        label: action.label,
                        iconName: action.iconName,
                        onActionClick: () => {},
                      }}
                      objectMetadata={objectMetadata}
                      rowData={recordData}
                    />
                  );
                }
                return (
                  <MenuItem
                    key={index}
                    disabled={
                      action?.permission ===
                      LifeCycleStageUserActionsPermissionEnum.VIEW
                    }
                    onClick={() => callUserActions(action)}
                    data-testid={`context-menu-option-${createTestAttribute(
                      action.label
                    )}`}
                    className={newStyles.actionMenuItem}
                  >
                    <div className={newStyles.itemContent}>
                      <Icon iconName={action.iconName as FluentIconNames} />
                      {action.label}
                    </div>
                  </MenuItem>
                );
              })}
          </MenuList>
        </MenuPopover>
      </Menu>
      <UserActionPopup
        open={isConfirmDialogOpen}
        onConfirmClicked={handleOnConfirmPopUp}
        onCancelClicked={() => {
          setRespectiveUserActionRes(undefined);
          setIsConfirmDialogOpen(false);
        }}
        objectName={objectName}
        title={respectiveUserActionRes?.label ?? ''}
        primaryButtonText={
          respectiveUserActionRes?.actionType === ActionTypeEnum.CANCEL
            ? LocalizationString.YES
            : LocalizationString.CHECK_IN
        }
        secondaryButtonText={
          respectiveUserActionRes?.actionType === ActionTypeEnum.CANCEL
            ? LocalizationString.NO
            : LocalizationString.CANCEL
        }
        attributeConfig={props.attributeConfig ?? ({} as ObjectAttributeType)}
        userActionData={respectiveUserActionRes}
        recordName={nameAndVersion?.name}
        version={nameAndVersion?.version}
        isTextRequired={isShowText}
        recordDetails={getFormValues(
          props.attributeConfig ?? ({} as ObjectAttributeType),
          props.recordDetails ?? ({} as Record<string, unknown>)
        )}
      />
      <DelegationModal
        isOpen={openDelegateModal}
        onClose={closeDelegateModal}
        rowItem={delegationModalRowItem}
        objectName={objectName}
      />
      {extensionRequestActionType && (
        <ExtensionRequestDialog
          isOpen={openExtensionRequestDialog}
          onClose={closeExtensionRequestDialog}
          rowItem={{
            ...response,
            name: recordName,
            version: response?.version,
          }}
          objectName={objectName}
          actionType={extensionRequestActionType}
        />
      )}
    </>
  );
};
